import { useCallback, useEffect, useRef } from 'preact/hooks';
import {
  BannersConfig,
  CommonPaymentBannerTrackingAttributes,
  ExtraTrackingAttrs,
  TrackingEventAttributes,
  TrackingEventName
} from '../types';
import { useApi, AtRiskPayment } from '../api';
import { getUpdateAction } from './UpdatePaymentButton';
import { getMessageDetails } from './PaymentAlert';

// Fires paymentbanner_shown automatically when payments load, and returns a track() function
// for user-triggered events that merges the common payload automatically.
export function usePaymentBannerTracking({
  atRiskPayment,
  config
}: {
  atRiskPayment: AtRiskPayment | null;
  config: BannersConfig;
}) {
  const api = useApi();
  const shownFired = useRef(false);

  const track = useCallback(
    async <EventName extends TrackingEventName>(event: EventName, extraAttrs?: ExtraTrackingAttrs[EventName]) => {
      if (!atRiskPayment) return;
      try {
        await api.sendTrackingEvent(event, {
          ...buildCommonTrackingAttrs(atRiskPayment, config),
          ...extraAttrs
        } as TrackingEventAttributes[EventName]);
      } catch {
        // tracking errors are non-fatal
      }
    },
    [api, atRiskPayment, config]
  );

  useEffect(() => {
    // if no at risk payment, we don't show the banner
    // this is also true if the banner has been dismissed, since we skip the API call
    if (!atRiskPayment || shownFired.current) return;
    shownFired.current = true; // for safety, make sure we don't accidentally double fire this event (e.g. if effect dependencies change)
    track('paymentbanner_shown', { userAgent: navigator.userAgent });
  }, [track, atRiskPayment]);

  return track;
}

function buildCommonTrackingAttrs(
  payment: AtRiskPayment,
  config: BannersConfig
): CommonPaymentBannerTrackingAttributes {
  return {
    paymentId: payment.payment,
    actionType: getUpdateAction(config)?.trackingType ?? null,
    messageType: getMessageDetails(payment).messageType,
    lastOrderId: payment.last_attempt?.order ?? null,
    nextOrderId: payment.next_retry?.order ?? null,
    currentUrl: window.location.origin + window.location.pathname,
    reasons: payment.risk.reasons
  };
}
